import { useState, useEffect, useRef } from 'react'

import { Select, ORIENTATION } from '../../../common/components/select'
import { reportLayoutService } from '../report-layout.service'
import { NotificationManager } from '../../../common/components/Notifications'
import { ControlBar } from '../control-bar'
import { blockDataService } from '../block-data.service'
import { sankeyBlockService } from './sankey-block.service'

import { useStyles } from './sankey-block.style'
import { SankeyBlockWidget } from './sankey-block-widget'

const NONE_OPTION = { value: 'none', label: 'None' }
const DEFAULT_WIDTH = 1024
const DEFAULT_SANKEY_HEIGHT = 500
const LEGEND_HEIGHT = 78

export const SankeyBlock = function ({
  blockKey,
  dataKey,
  report,
  strataDefinition,
  filterDefinition,
  sliderDefinition,
  parentControlBar,
  parentBlock,
  parentBlockSelections,
  useControlBar = true,
  width = DEFAULT_WIDTH,
  height = width / (DEFAULT_WIDTH / DEFAULT_SANKEY_HEIGHT) + LEGEND_HEIGHT,
  ...props
}) {
  const classes = useStyles()

  const primaryFilter = filterDefinition && filterDefinition.fields && filterDefinition.fields.find((filter) => (filter.isPrimary))
  const filterCategory = filterDefinition && filterDefinition.fields && [NONE_OPTION].concat(
    filterDefinition.fields
      .filter((filter) => (!filter.isPrimary))
      .map((filter) => ({ value: filter.key, label: filter.label }))
  )

  const [datasets, setDatasets] = useState()
  const [selectedDisplayOption, setSelectedDisplayOption] = useState(reportLayoutService.getDefaultValue(primaryFilter))
  const [selectedFilterCategory, setSelectedFilterCategory] = useState(filterCategory && filterCategory[0] && filterCategory[0].value)
  const [selectedFilterCategoryValue, setSelectedFilterCategoryValue] = useState()
  const [filterCategoryOptions, setFilterCategoryOptions] = useState()
  const [selections, setSelections] = useState()
  const [labels, setLabels] = useState({})

  const paramsToServer = useRef()

  function isFirstDataLoad () {
    return false
  }

  function fetchSankeyData () {
    paramsToServer.current = {
      strata: undefined, // sending no strata as we are not doing segmentation at the moment
      filters: (selectedFilterCategory === NONE_OPTION.value)
        ? {}
        : {
          [selectedFilterCategory]: {
            label: selectedFilterCategory,
            value: selectedFilterCategoryValue
          }
        }
    }

    if (primaryFilter) {
      Object.assign(paramsToServer.current.filters, { [primaryFilter.key]: selectedDisplayOption })
    }

    setSelections(paramsToServer.current)
  }

  function onDataLoad (data) {
    const url = `api/report/${report.id}/${blockKey}`

    let dataList = props.splitChart ? data : [data]
    let rawData = [...dataList.map((data) => data && data[0] && data[0].data)]

    let newDatasets = []
    for (let data of rawData) {
      if (!data || data.length < 1) {
        setDatasets(Array(dataList.length).fill(undefined))
        return
      } else if (Array.isArray(data) && data.length > 0) {
        const filteredData = filterOutBadData(data, url, paramsToServer.current)
        newDatasets.push(filteredData)

        if (filteredData.length !== data.length) {
          NotificationManager.create({ type: 'warning', timeOut: 20000, message: 'Sorry, this chart is using partial data as some bad data had to be filtered out.' })
        }
      }
    }

    updateLabels(props)
    setDatasets(newDatasets)
  }

  function updateLabels (props) {
    const labels = sankeyBlockService.getLabels(report, blockKey, props)
    setLabels(labels)
  }

  useEffect(() => {
    fetchSankeyData()
  }, [selectedDisplayOption])

  useEffect(() => {
    if (!selectedFilterCategory) {
      return
    }

    const filterCategory = filterDefinition.fields.find((filter) => (filter.key === selectedFilterCategory))
    const filterCategoryOptions = filterCategory && filterCategory.options && filterCategory.options.slice(1)
    setFilterCategoryOptions(filterCategoryOptions)
    setSelectedFilterCategoryValue()
  }, [selectedFilterCategory, filterDefinition])

  useEffect(() => {
    if (!isFirstDataLoad() && (selectedFilterCategoryValue || selectedFilterCategory === NONE_OPTION.value)) {
      fetchSankeyData()
    }
  }, [selectedFilterCategoryValue])

  useEffect(() => {
    updateLabels(props)
  }, [props.stateLabels, props.stageLabels])

  if (useControlBar) {
    return (
      <SankeyBlockWithControlBar
        blockKey={blockKey}
        dataKey={dataKey}
        report={report}
        strataDefinition={strataDefinition}
        filterDefinition={filterDefinition}
        sliderDefinition={sliderDefinition}
        parentControlBar={parentControlBar}
        parentBlock={parentBlock}
        parentBlockSelections={parentBlockSelections}
        width={width}
        height={height}
        {...props}
        {...labels}
      />
    )
  } else {
    return <div className="report-full-width">
      {(primaryFilter || filterCategory) &&
        <div className={classes.wrapper}>
          {primaryFilter && primaryFilter.options && (
            <div className={classes.dropdownContainer}>
              <Select
                label="Display"
                orientation={ORIENTATION.HORIZONTAL}
                size="medium"
                options={primaryFilter.options}
                value={primaryFilter.options.find(({ value }) => value === selectedDisplayOption)}
                onChange={({ value }) => {
                  setSelectedDisplayOption(value)
                }} />
            </div>
          )}
          <div className={classes.filterContainer}>
            {filterCategory && filterCategory.length > 1 && (
              <div className={classes.filterCategory}>
                <Select
                  label="Filter"
                  orientation={ORIENTATION.HORIZONTAL}
                  size="medium"
                  options={filterCategory}
                  value={filterCategory.find(({ value }) => value === selectedFilterCategory)}
                  onChange={({ value }) => {
                    setSelectedFilterCategory(value)
                  }} />
              </div>
            )}
            {filterCategoryOptions && (
              <div className={classes.filterCategoryValue}>
                <Select
                  size="medium"
                  options={filterCategoryOptions}
                  value={filterCategoryOptions.find(({ value }) => value === selectedFilterCategoryValue) || null}
                  onChange={({ value }) => {
                    setSelectedFilterCategoryValue(value)
                  }} />
              </div>
            )}
          </div>
        </div>
      }
      <SankeyBlockWidget
        report={report}
        blockKey={blockKey}
        dataKey={dataKey}
        caption={props.caption}
        sliderDefinition={sliderDefinition}
        filterDefinition={filterDefinition}
        selections={selections}
        splitChart={props.splitChart}
        width={width}
        height={height}
        onDataLoad={onDataLoad}
        datasets={datasets}
        labels={labels}
        stateLabels={props.stateLabels}
        {...props}
      />
    </div>
  }
}

const SankeyBlockWithControlBar = function ({
  blockKey,
  dataKey,
  report,
  strataDefinition,
  filterDefinition,
  sliderDefinition,
  parentSelections,
  width = DEFAULT_WIDTH,
  height = width / (DEFAULT_WIDTH / DEFAULT_SANKEY_HEIGHT) + LEGEND_HEIGHT,
  ...props
}) {
  const rawData = useRef([])

  const [datasets, setDatasets] = useState()
  const [selections, setSelections] = useState()
  const [labels, setLabels] = useState({})
  const [propOverrides, setPropOverrides] = useState()

  props = reportLayoutService.applyPropOverrides(props, propOverrides)

  useEffect(() => {
    updateLabels(props)
  }, [props.stateLabels, props.stageLabels])

  function updateLabels (props) {
    const labels = sankeyBlockService.getLabels(report, blockKey, props)
    setLabels(labels)
  }

  function onDataLoad (data) {
    let dataList = props.splitChart ? data : [data]
    rawData.current = [...dataList.map((data) => (data && data[0] && data[0].data))]

    for (let data of dataList) {
      if (!data || data.length < 1) {
        rawData.current = Array(dataList.length).fill(undefined)
        updateChart(selections)
        return
      }
    }

    updateLabels(props)
    updateChart(selections)
  }

  function updateChart (currentSelections) {
    if (rawData.current && rawData.current.length) {
      const url = `api/report/${report.id}/${blockKey}`

      let hasBadData = false
      const newDatasets = rawData.current.map((data) => {
        if (data) {
          const filteredData = blockDataService.filterBlockDataByKeyValue(data, sliderDefinition && sliderDefinition.key, currentSelections.slider?.value)
          const filteredBadData = filterOutBadData(filteredData, url, currentSelections)
          if (filteredBadData.length !== filteredData.length) {
            hasBadData = true
          }
          return filteredData
        }
      })
      setDatasets(newDatasets)

      if (hasBadData) {
        NotificationManager.create({ type: 'warning', timeOut: 20000, message: 'Sorry, this chart is using partial data as some bad data had to be filtered out.' })
      }
    }
  }

  function onControlBarChange (selections, changes) {
    if (changes.props) {
      setPropOverrides(selections.props)
    }

    if (changes.slider) {
      updateChart(selections)
    } else {
      setSelections(selections)
    }
  }

  return (
    <div className="report-full-width">
      <ControlBar
        strataDefinition={strataDefinition}
        sliderDefinition={sliderDefinition}
        filterDefinition={filterDefinition}
        parentSelections={parentSelections}
        onChange={onControlBarChange} />
      <div>
        <SankeyBlockWidget
          report={report}
          blockKey={blockKey}
          dataKey={dataKey}
          caption={props.caption}
          sliderDefinition={sliderDefinition}
          filterDefinition={filterDefinition}
          selections={selections}
          splitChart={props.splitChart}
          width={width}
          height={height}
          onDataLoad={onDataLoad}
          datasets={datasets}
          labels={labels}
          stateLabels={props.stateLabels}
          {...props}
        />
      </div>
    </div>
  )
}

function filterOutBadData (data, url, params) {
  return data.filter((row) => {
    const isValidData = row.every((value) => (typeof value === 'number'))
    if (!isValidData) {
      console.error('Invalid Sankey data encountered', row, url, params)
    }
    return isValidData
  })
}
